import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { checkUsernameAvailability, getProfile, updateProfile } from '../../api/client';

const MAX_DATA_URL_LENGTH = 500000;
const USERNAME_RE = /^[A-Za-z0-9._-]{3,32}$/;
type UsernameStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid' | 'unchanged';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export default function Profile() {
  const navigate = useNavigate();
  const { username, avatarUrl, setAvatarUrl, isLoggedIn, refreshProfile } = useAuth();
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoUrlInput, setPhotoUrlInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState('');
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [originalUsername, setOriginalUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle');
  const [usernameHint, setUsernameHint] = useState('');

  const [userInfo, setUserInfo] = useState({
    username: '',
    name: '',
    email: '',
    location: '',
    bio: '',
    joinDate: '',
  });

  // Track last-saved values to diff on blur
  const savedValuesRef = useRef({ username: '', name: '', email: '', location: '', bio: '' });

  const [activity, setActivity] = useState<Array<{
    id: number; activity_type: string; created_at: string;
    playlist_name?: string | null; track_title?: string | null;
  }>>([]);

  const [stats, setStats] = useState({
    totalPlaylists: 0, totalTracks: 0, totalListeningTime: '0m',
    followers: 0, following: 0, likedTracks: 0, likedPlaylists: 0,
  });

  useEffect(() => {
    if (!isLoggedIn) { setLoading(false); return; }
    getProfile()
      .then((profile) => {
        const created = profile.created_at ? new Date(profile.created_at) : null;
        const info = {
          username: profile.username || '',
          name: profile.display_name || profile.username || '',
          email: profile.email || '',
          location: profile.location || '',
          bio: profile.bio || '',
          joinDate: created ? created.toLocaleString('default', { month: 'long', year: 'numeric' }) : '',
        };
        setUserInfo(info);
        setOriginalUsername(profile.username || '');
        savedValuesRef.current = {
          username: info.username,
          name: info.name,
          email: info.email,
          location: info.location,
          bio: info.bio,
        };
        setStats({
          totalPlaylists: profile.stats?.totalPlaylists ?? 0,
          totalTracks: profile.stats?.totalTracks ?? 0,
          totalListeningTime: `${profile.stats?.totalListeningTimeMinutes ?? 0}m`,
          followers: profile.stats?.followers ?? 0,
          following: profile.stats?.following ?? 0,
          likedTracks: profile.stats?.likedTracks ?? 0,
          likedPlaylists: profile.stats?.likedPlaylists ?? 0,
        });
        setActivity((profile.activity || []).slice(0, 10));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isLoggedIn]);

  // Username availability check (debounced)
  useEffect(() => {
    const value = userInfo.username.trim();
    if (!value) { setUsernameStatus('invalid'); setUsernameHint('Username is required.'); return; }
    if (value === originalUsername) { setUsernameStatus('unchanged'); setUsernameHint(''); return; }
    if (!USERNAME_RE.test(value) || value.includes('@')) {
      setUsernameStatus('invalid');
      setUsernameHint('Use 3-32 chars: letters, numbers, dot, underscore, hyphen.');
      return;
    }
    setUsernameStatus('checking');
    setUsernameHint('Checking availability...');
    const timer = setTimeout(async () => {
      try {
        const result = await checkUsernameAvailability(value);
        if (result.available) {
          setUsernameStatus('available');
          setUsernameHint(result.message || 'Username is available');
        } else {
          setUsernameStatus('unavailable');
          setUsernameHint(result.message || 'Username is not available');
        }
      } catch {
        setUsernameStatus('unavailable');
        setUsernameHint('Could not validate username right now');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [originalUsername, userInfo.username]);

  const flashSaved = useCallback(() => {
    setSaveStatus('saved');
    setSaveError('');
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2500);
  }, []);

  // Generic blur-save for simple text fields
  const handleBlurSave = useCallback(async (field: 'name' | 'email' | 'location' | 'bio') => {
    const current = userInfo[field].trim();
    const prev = savedValuesRef.current[field];
    if (current === prev) return;
    setSaveStatus('saving');
    try {
      await updateProfile({ [field === 'name' ? 'display_name' : field]: current || null });
      savedValuesRef.current[field] = current;
      flashSaved();
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    }
  }, [userInfo, flashSaved]);

  // Username blur-save: only when valid and available
  const handleUsernameBlur = useCallback(async () => {
    const current = userInfo.username.trim();
    if (usernameStatus !== 'available' && usernameStatus !== 'unchanged') return;
    if (current === savedValuesRef.current.username) return;
    setSaveStatus('saving');
    try {
      const result = await updateProfile({ username: current });
      savedValuesRef.current.username = current;
      setOriginalUsername(current);
      if (result && (result as { token?: string }).token) await refreshProfile();
      flashSaved();
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Failed to save username');
    }
  }, [userInfo.username, usernameStatus, flashSaved, refreshProfile]);

  const handleInputChange = (field: string, value: string) => {
    setUserInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) { setPhotoError('Please choose an image file (e.g. JPG, PNG).'); return; }
    setPhotoError('');
    setPhotoLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (dataUrl.length > MAX_DATA_URL_LENGTH) {
        setPhotoError('Image is too large. Use a smaller file or a URL instead.');
        setPhotoLoading(false);
        return;
      }
      setAvatarUrl(dataUrl).then(() => setPhotoLoading(false)).catch(() => { setPhotoError('Failed to update photo.'); setPhotoLoading(false); });
    };
    reader.onerror = () => { setPhotoError('Could not read file.'); setPhotoLoading(false); };
    reader.readAsDataURL(file);
  };

  const handleSetUrl = () => {
    const url = photoUrlInput.trim();
    if (!url) return;
    setPhotoError('');
    setPhotoLoading(true);
    setAvatarUrl(url).then(() => { setPhotoUrlInput(''); setPhotoLoading(false); }).catch(() => { setPhotoError('Failed to update photo. Check the URL.'); setPhotoLoading(false); });
  };

  const handleRemovePhoto = () => {
    setPhotoError('');
    setPhotoLoading(true);
    setAvatarUrl(null).then(() => setPhotoLoading(false)).catch(() => { setPhotoError('Failed to remove photo.'); setPhotoLoading(false); });
  };

  const inputCls = 'flex-1 rounded-lg border border-transparent bg-transparent px-2 py-1 text-sm hover:border-orange-200 focus:border-orange-400 focus:bg-orange-50/40 focus:outline-none transition-colors';

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-red-50">
      <div className="fixed top-14 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-orange-100 z-50">
        <div className="px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-full"
          >
            <i className="ri-arrow-left-line text-orange-600 text-sm" aria-hidden />
          </button>
          <h1 className="text-lg font-semibold text-gray-800">Profile</h1>
          {/* Save status indicator */}
          <div className="w-20 flex justify-end">
            {saveStatus === 'saving' && (
              <span className="text-xs text-amber-600 animate-pulse">Saving…</span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-xs text-emerald-600 font-medium">✓ Saved</span>
            )}
            {saveStatus === 'error' && (
              <span className="text-xs text-red-600">Error</span>
            )}
          </div>
        </div>
      </div>

      <div className="pt-32 pb-8 px-4">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading profile...</div>
        ) : (
          <>
            {saveError && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {saveError}
              </div>
            )}

            {/* Profile Picture & Basic Info */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mr-4 overflow-hidden shrink-0">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <i className="ri-user-line text-white text-3xl" aria-hidden />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={userInfo.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onBlur={() => handleBlurSave('name')}
                    className="text-xl font-bold text-gray-800 rounded-lg border border-transparent bg-transparent px-2 py-1 w-full mb-1 hover:border-orange-200 focus:border-orange-400 focus:bg-orange-50/40 focus:outline-none transition-colors"
                    placeholder="Display name"
                  />
                  <p className="text-xs text-gray-500 truncate pl-2">@{userInfo.username || username || 'set-username'}</p>
                  <p className="text-sm text-gray-500 pl-2">Member since {userInfo.joinDate}</p>
                </div>
              </div>

              {/* Profile photo controls */}
              <div className="mt-4 pt-4 border-t border-orange-100">
                <p className="text-sm font-medium text-gray-700 mb-2">Profile photo</p>
                {photoError && <p className="text-sm text-red-600 mb-2">{photoError}</p>}
                <div className="flex flex-wrap gap-2 items-center">
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                  <button type="button" disabled={photoLoading} onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium bg-orange-100 text-orange-800 hover:bg-orange-200 disabled:opacity-50">
                    {photoLoading ? 'Updating…' : 'Change photo'}
                  </button>
                  <input
                    type="url"
                    placeholder="Or paste image URL"
                    value={photoUrlInput}
                    onChange={(e) => setPhotoUrlInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSetUrl()}
                    className="flex-1 min-w-[140px] rounded-lg border border-orange-200 px-3 py-1.5 text-sm"
                  />
                  <button type="button" disabled={photoLoading} onClick={handleSetUrl}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-50">
                    Use URL
                  </button>
                  {avatarUrl && (
                    <button type="button" disabled={photoLoading} onClick={handleRemovePhoto}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50">
                      Remove photo
                    </button>
                  )}
                </div>
              </div>

              <textarea
                value={userInfo.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                onBlur={() => handleBlurSave('bio')}
                className="w-full mt-4 rounded-lg border border-transparent bg-transparent px-2 py-2 text-sm text-gray-600 resize-none hover:border-orange-200 focus:border-orange-400 focus:bg-orange-50/40 focus:outline-none transition-colors"
                rows={3}
                placeholder="Bio (click to edit)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              {[
                { label: 'Playlists', value: stats.totalPlaylists },
                { label: 'Tracks', value: stats.totalTracks },
                { label: 'Followers', value: stats.followers },
                { label: 'Following', value: stats.following },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-2xl font-bold text-orange-600 mb-1">{value}</div>
                  <div className="text-xs text-gray-500">{label}</div>
                </div>
              ))}
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
              <div className="space-y-4">
                {/* Username */}
                <div>
                  <div className="flex items-center">
                    <i className="ri-at-line text-orange-600 w-5 h-5 mr-3 shrink-0" aria-hidden />
                    <input
                      type="text"
                      value={userInfo.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      onBlur={handleUsernameBlur}
                      className={inputCls}
                      placeholder="username"
                    />
                  </div>
                  {usernameHint && (
                    <p className={`mt-1 ml-8 text-xs ${
                      usernameStatus === 'available' || usernameStatus === 'unchanged'
                        ? 'text-emerald-700'
                        : usernameStatus === 'checking'
                          ? 'text-amber-700'
                          : 'text-red-600'
                    }`}>
                      {usernameHint}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="flex items-center">
                  <i className="ri-mail-line text-orange-600 w-5 h-5 mr-3 shrink-0" aria-hidden />
                  <input
                    type="email"
                    value={userInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onBlur={() => handleBlurSave('email')}
                    className={inputCls}
                    placeholder="Email address"
                  />
                </div>

                {/* Location */}
                <div className="flex items-center">
                  <i className="ri-map-pin-line text-orange-600 w-5 h-5 mr-3 shrink-0" aria-hidden />
                  <input
                    type="text"
                    value={userInfo.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    onBlur={() => handleBlurSave('location')}
                    className={inputCls}
                    placeholder="Location"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h3>
              {activity.length === 0 ? (
                <p className="text-sm text-gray-500">No recent activity yet.</p>
              ) : (
                <div className="space-y-3">
                  {activity.map((item) => (
                    <div key={item.id} className="flex items-center py-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-pulse-line text-orange-600 text-sm" aria-hidden />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-800">
                          {item.activity_type.replaceAll('.', ' ')}
                          {item.playlist_name ? ` • ${item.playlist_name}` : ''}
                          {item.track_title ? ` • ${item.track_title}` : ''}
                        </p>
                        <p className="text-xs text-gray-500">{new Date(item.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
